import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Lock, Zap } from "lucide-react";
import api, { COUNTRY_FLAGS } from "@/lib/api";
import Seo from "@/components/Seo";

const SITE_ORIGIN =
  typeof window !== "undefined" ? window.location.origin : "https://giftopay.com";

const COUNTRY_COPY = {
  US: { title: "Sell Gift Cards for Cash in the United States", currency: "USD" },
  UK: { title: "Sell Gift Cards for Cash in the United Kingdom", currency: "GBP" },
  AE: { title: "Sell Gift Cards for Cash in the UAE", currency: "AED" },
  CA: { title: "Sell Gift Cards for Cash in Canada", currency: "CAD" },
};

export default function CountryLanding() {
  const { code: rawCode } = useParams();
  const code = (rawCode || "").toUpperCase();
  const [brands, setBrands] = useState([]);
  const [methods, setMethods] = useState([]);
  const [country, setCountry] = useState(null);

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data));
    api.get(`/catalog/payout-methods?country=${code}`).then((r) => setMethods(r.data));
    api.get("/catalog/countries").then((r) => setCountry(r.data.find((c) => c.code === code)));
  }, [code]);

  const sortedBrands = useMemo(() => {
    return [...brands].sort((a, b) => (b.rate[code] || 0) - (a.rate[code] || 0));
  }, [brands, code]);

  const meta = COUNTRY_COPY[code];

  if (!meta) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-center px-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#0B3C5D]">Country not supported</h1>
          <p className="mt-2 text-slate-600 text-sm">
            Giftopay is currently available in the US, UK, UAE & Canada.{" "}
            <Link to="/" className="underline text-[#00C853]">Back to homepage</Link>.
          </p>
        </div>
      </div>
    );
  }

  const title = meta.title;
  const description = `Sell 45+ gift card brands from ${country?.name || code} on Giftopay. Live ${meta.currency} rates, secure submissions, reviewed payouts via PayPal, Bank Transfer, Crypto and more.`;
  const canonical = `${SITE_ORIGIN}/sell-in/${code.toLowerCase()}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": title,
    "description": description,
    "areaServed": country?.name || code,
    "provider": { "@type": "Organization", "name": "Giftopay", "url": SITE_ORIGIN },
  };

  return (
    <div className="min-h-screen" data-testid="country-landing">
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-gradient-to-br from-[#F8FAFC] to-white pt-16 pb-12">
        <div className="absolute inset-0 grid-pattern opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-[#0B3C5D]">
            <span className="text-base">{COUNTRY_FLAGS[code]}</span>
            {country?.name || code} · {meta.currency}
          </div>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B3C5D] leading-[1.05] tracking-tight">
            {title}
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            Live {meta.currency} rates on Amazon, Apple, Walmart, Steam, Visa and 40+ more.
            Payouts within 60 minutes to 24 hours via methods available in {country?.name || code}.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={`/sell?country=${code}`}
              className="inline-flex items-center justify-center rounded-full bg-[#0B3C5D] text-white px-7 py-3.5 text-base font-semibold btn-primary"
              data-testid="country-cta-sell"
            >
              Start Selling <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/rates"
              className="inline-flex items-center justify-center rounded-full bg-white border border-slate-200 text-[#0B3C5D] px-7 py-3.5 text-base font-semibold hover:border-[#0B3C5D]"
            >
              See all rates
            </Link>
          </div>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-5 text-xs text-slate-600">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#00C853]" /> Reviewed Payouts</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="w-4 h-4 text-[#00C853]" /> 256-bit SSL</span>
            <span className="inline-flex items-center gap-1.5"><Zap className="w-4 h-4 text-[#00C853]" /> Paid in 1–24h</span>
          </div>
        </div>
      </section>

      {/* Payout methods for this country */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0B3C5D] tracking-tight">
            Payout methods available in {country?.name || code}
          </h2>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {methods.map((m) => (
              <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`country-payout-${m.id}`}>
                <p className="font-display font-bold text-sm text-[#0B3C5D]">{m.name}</p>
                <p className="text-[11px] text-slate-500 mt-0.5">ETA: {m.eta}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top brands in this country */}
      <section className="py-14 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#0B3C5D] tracking-tight">
            Top {meta.currency} rates today
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sortedBrands.slice(0, 18).map((b, i) => (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: Math.min(i * 0.02, 0.3) }}
              >
                <Link
                  to={`/sell/${b.id}`}
                  className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#0B3C5D] hover:shadow-md transition"
                  data-testid={`country-brand-${b.id}`}
                >
                  <div>
                    <p className="font-display font-bold text-sm text-[#0B3C5D]">{b.name}</p>
                    <p className="text-[11px] text-slate-500">{b.category}</p>
                  </div>
                  <span className="font-display font-bold text-[#00C853]">
                    {(b.rate[code] * 100).toFixed(1)}%
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
